import {useLocale} from "../context/LocaleContext";
import React, { useEffect, useMemo, useState } from 'react';
import { api, useAuth } from '../context/AuthContext';
import { formErrorMessage } from '../formFeedback';
import {phoneCountries, internationalPhone} from '../phoneNumbers';

export default function WhatsAppLogin({ onSuccess }) {
 const {t: translate, locale: currentLocale}=useLocale();

  const { loginWithWhatsApp } = useAuth();
  const [available, setAvailable] = useState(null);
  const [phone, setPhone] = useState('');
  const [country, setCountry] = useState('TR');
  const countries = useMemo(() => phoneCountries(currentLocale), [currentLocale]);
  const callingCode = countries.find(item => item.country === country)?.code || '+90';
  const [consent, setConsent] = useState(false);
  const [challenge, setChallenge] = useState(null);
  const [code, setCode] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => {
    let active = true;
    api.get('/auth/whatsapp/status').then(r => { if (active) setAvailable(r.data?.available === true); })
      .catch(() => { if (active) setAvailable(false); });
    return () => { active = false; };
  }, []);
  async function submit(e) {
    e.preventDefault();
    if (busy || !available) return;
    setBusy(true); setError('');
    try {
      if (challenge) {
        const name = `${firstName.trim()} ${lastName.trim()}`.replace(/\s+/g, ' ').trim();
        if (!firstName.trim() || !lastName.trim() || name.length > 80) {
          setError('Adınızı ve soyadınızı girin; toplam en fazla 80 karakter olabilir.'); return;
        }
        await loginWithWhatsApp(challenge, code, name);
        setCode(''); onSuccess();
      } else {
        if (!consent) { setError('WhatsApp giriş kodu onayını işaretleyin.'); return; }
        const parsed = internationalPhone(phone, country);
        if (!parsed) { setError('Ülkeyi seçip geçerli bir cep telefonu numarası girin.'); return; }
        const r = await api.post('/auth/whatsapp/send', { phone: parsed.number, consent });
        if (r.data?.accepted !== true || !/^[a-f0-9]{32}$/.test(r.data?.challenge_id || '')) {
          throw new Error('Kod isteği doğrulanamadı.');
        }
        setChallenge(r.data.challenge_id); setCode('');
      }
    } catch (err) { setError(formErrorMessage(err, 'WhatsApp ile giriş tamamlanamadı. Lütfen tekrar deneyin.')); }
    finally { setBusy(false); }
  }
  return <section aria-label={translate("WhatsApp ile telefon girişi")} className="mb-5 border rounded-lg p-3" style={{ borderColor: 'var(--sy-border)', color: 'var(--sy-text)' }}>
    <h2 className="font-semibold">{translate("WhatsApp ile giriş")}</h2>
    {available !== true ? <p role="status" className="text-sm mt-2">{available === null ? translate("Kullanılabilirlik kontrol ediliyor…") : translate("WhatsApp ile giriş henüz kullanılamıyor. E-posta veya Google ile devam edebilirsiniz.")}</p> :
      <form onSubmit={submit} className="grid gap-3 mt-3">
        {!challenge ? <>
          <label htmlFor="wa-country">{translate("Ülke ve telefon kodu")}</label>
          <select id="wa-country" autoComplete="country" value={country} onChange={e => setCountry(e.target.value)} disabled={busy}>
            {countries.map(item => <option key={item.country} value={item.country}>{`${item.name} (${item.code})`}</option>)}
          </select>
          <label htmlFor="wa-phone">{translate("Cep telefonu")}</label>
          <div className="sy-wa-phone" dir="ltr"><span aria-label={translate("Ülke kodu")}>{callingCode}</span>
            <input id="wa-phone" type="tel" autoComplete="tel-national" required minLength={6} maxLength={30} value={phone} onChange={e => {
              const value = e.target.value; setPhone(value);
              if (/^\s*(\+|00)/.test(value)) {
                const parsed = internationalPhone(value, country);
                if (parsed) {
                  const detected = parsed.country || countries.find(item => item.code === '+' + parsed.countryCallingCode)?.country;
                  if (detected) { setCountry(detected); setPhone(parsed.nationalNumber); }
                }
              }
            }} disabled={busy} placeholder={country === 'TR' ? '5xx xxx xx xx' : translate("Telefon numaranız")} aria-describedby="wa-phone-help" className="rounded border p-2 bg-transparent" />
          </div>
          <small id="wa-phone-help">{translate("Ülke kodu otomatik eklenir. Cep telefonu numaranızı girin.")}</small>
          <label className="sy-wa-consent" htmlFor="wa-consent">
            <input id="wa-consent" type="checkbox" checked={consent} onChange={e => setConsent(e.target.checked)} disabled={busy} required />
            <span><strong>{translate("WhatsApp giriş kodu onayı")}</strong><span>{translate("Giriş kodumu bu numaraya WhatsApp üzerinden almayı kabul ediyorum.")}</span></span>
          </label>
          <p className="text-sm">Mevcut hesabınız varsa önce o hesaba giriş yapın. Telefonunuzu “Giriş yöntemlerim” bölümünden ekleyin; kayıtlarınız aynı hesapta kalsın.</p>
        </> : <>
          <p role="status" className="text-sm">{translate("Kod isteğiniz kabul edildi. WhatsApp’a gelen kod 5 dakika geçerlidir.")}</p>
          <label htmlFor="wa-first-name">{translate("Adınız")}</label>
          <input id="wa-first-name" autoComplete="given-name" required maxLength={60} value={firstName} onChange={e => setFirstName(e.target.value)} disabled={busy} />
          <label htmlFor="wa-last-name">{translate("Soyadınız")}</label>
          <input id="wa-last-name" autoComplete="family-name" required maxLength={60} value={lastName} onChange={e => setLastName(e.target.value)} disabled={busy} />
          <small>{translate("Adınız ve soyadınız, telefonunuz doğrulandıktan sonra yeni veya eksik profilinize kaydedilir. Mevcut hesap bilgileriniz korunur.")}</small>
          <label htmlFor="wa-code">{translate("6 haneli giriş kodu")}</label>
          <input id="wa-code" inputMode="numeric" autoComplete="one-time-code" pattern="[0-9]{6}" maxLength={6} required value={code} onChange={e => setCode(e.target.value)} disabled={busy} className="rounded border p-2 bg-transparent" />
          <button type="button" disabled={busy} onClick={() => { setChallenge(null); setCode(''); setError(''); }}>{translate("Numarayı değiştir / yeni kod iste")}</button>
        </>}
        {error && <p role="alert">{error}</p>}
        <button type="submit" disabled={busy || (!challenge && !consent)} className="rounded border p-2 font-semibold" style={{ borderColor: 'var(--sy-border)' }}>{busy ? translate("İşleniyor…") : challenge ? translate("Kodu doğrula ve giriş yap") : translate("WhatsApp kodu iste")}</button>
      </form>}
  </section>;
}
